import {
  Checkbox,
  Divider,
  Flex,
  FormikField,
  LegacyText,
  PrimaryButton,
  Stack,
  Text,
} from "flicket-ui";
import { FormikHelpers, FormikProvider, useFormik } from "formik";
import { useRouter } from "next/router";
import React, { ChangeEvent, useEffect, useState } from "react";
import { SlateRenderer } from "~components/common";
import { marketingOptInCopy } from "~config";
import {
  initialValues,
  SavedFormValues,
  validationSchema,
} from "~form/waitlist";
import { CustomFieldOuput } from "~graphql/sdk";
import { useAccount, useOrganization } from "~hooks";
import { WaitListFormValues } from "./interface";

type WaitlistFormProps = {
  handleSubmit: (
    values: WaitListFormValues,
    actions: FormikHelpers<WaitListFormValues>
  ) => void;
  presaleInformation?: string;
  extraDropdown?: typeof FormikField;
  waitlistType: "resale" | "registration";
  customFields?: CustomFieldOuput[];
  hideSubmitButton?: boolean;
};

const hasSessionStorage = typeof sessionStorage !== "undefined";

export const waitlistFormId = "waitlist-form-form";

export const WaitlistForm = ({
  handleSubmit,
  presaleInformation,
  extraDropdown,
  waitlistType,
  customFields: customFieldsInput = [],
  hideSubmitButton = false,
}: WaitlistFormProps) => {
  const { organization } = useOrganization();
  const { user, isLoggedIn, logout } = useAccount();
  const initialOptIn = user
    ? !!(user.usableForEmailMarketing || user.usableForSMSMarketing)
    : !!organization?.defaultOptInMarketing;
  const router = useRouter();
  const [optIn, setOptIn] = useState(initialOptIn);
  const [customFields, setCustomFields] = useState(customFieldsInput);

  const savedValues = (() => {
    try {
      const rawValues =
        hasSessionStorage && sessionStorage.getItem("waitlist-saved-values");
      return rawValues && (JSON.parse(rawValues) as SavedFormValues);
    } catch (err) {
      console.error(err);
      return undefined;
    }
  })();

  const formik = useFormik({
    initialValues: initialValues(user, savedValues),
    onSubmit: (values, actions) => {
      handleSubmit(
        {
          ...values,
          customFields: customFields.map((customField) => ({
            id: customField.id,
            fieldName: customField.label,
            fieldValue: customField.value.toString(),
          })),
        },
        actions
      );
    },
    validationSchema,
    enableReinitialize: true,
  });

  const {
    errors,
    isSubmitting,
    submitCount,
    values,
    setFieldValue,
    handleSubmit: _handleSubmit,
  } = formik;

  useEffect(() => {
    if (isSubmitting) {
      const firstError = Object.keys(errors)[0];
      if (firstError) {
        document.getElementsByName(firstError)?.[0]?.scrollIntoView({
          behavior: "smooth",
        });
      }
    }
  }, [isSubmitting, errors]);

  return (
    <Flex flexDir="column" alignItems="center" id="waitlist-form">
      <FormikProvider value={formik}>
        <Flex
          as="form"
          onSubmit={_handleSubmit}
          flexDirection="column"
          role="form"
          id={waitlistFormId}
          width="100%"
        >
          {presaleInformation && (
            <>
              <SlateRenderer value={presaleInformation}></SlateRenderer>
              <Divider my={3} />
            </>
          )}
          <Stack
            gap={3}
            width="100%"
            mb={3}
            direction={{ _: "vertical", md: "horizontal" }}
          >
            <FormikField
              label={
                ((
                  <Text variant="formLabel.Bold">First name</Text>
                ) as unknown) as string
              }
              name="firstName"
              submitCount={submitCount}
              flex={1}
            />
            <FormikField
              label={
                ((
                  <Text variant="formLabel.Bold">Last name</Text>
                ) as unknown) as string
              }
              name="lastName"
              submitCount={submitCount}
              flex={1}
            />
          </Stack>

          <FormikField
            label={
              ((
                <Text variant="formLabel.Bold">Email</Text>
              ) as unknown) as string
            }
            name="email"
            mb={!isLoggedIn ? 3 : 1}
            submitCount={submitCount}
            disabled={isLoggedIn}
          />
          {isLoggedIn && (
            <LegacyText variant="demiBold.S" textAlign="center" mb={3}>
              Want to register with a different email?{" "}
              <a
                onClick={() => {
                  try {
                    hasSessionStorage &&
                      sessionStorage.setItem(
                        "waitlist-saved-values",
                        JSON.stringify(values)
                      );
                  } catch (err) {
                    console.error(err);
                  }
                  const url = router.asPath;
                  void logout(
                    url.includes("#waitlist-form")
                      ? url
                      : `${url}#waitlist-form`
                  );
                }}
              >
                Click here
              </a>
            </LegacyText>
          )}
          <FormikField
            label={
              ((
                <Text variant="formLabel.Bold">Phone</Text>
              ) as unknown) as string
            }
            name="phoneNumber"
            mb={3}
            submitCount={submitCount}
          />
          {extraDropdown?.(undefined)}
          <FormikField
            label={marketingOptInCopy(organization, "full")}
            name="usableForMarketing"
            as={Checkbox}
            mb={4}
            mt={2}
            checked={optIn}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setOptIn(e.target.checked);
              setFieldValue("usableForMarketing", e.target.checked).catch(
                console.error
              );
            }}
          />

          {customFields.map((customField, index) => (
            <FormikField
              key={customField.id}
              label={
                ((
                  <Text variant="formLabel.Bold">{customField.label}</Text>
                ) as unknown) as string
              }
              name={customField.id}
              as={Checkbox}
              mb={4}
              checked={customField.value}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setCustomFields((prevValues) => {
                  prevValues[index].value = e.target.checked;
                  return [...prevValues];
                })
              }
            />
          ))}

          {!hideSubmitButton && (
            <PrimaryButton
              type="submit"
              isLoading={isSubmitting}
              width="100%"
              lineHeight="normal"
              boxShadow="button"
            >
              {waitlistType === "registration" ? "Register" : "Join waitlist"}
            </PrimaryButton>
          )}
        </Flex>
      </FormikProvider>
    </Flex>
  );
};
